import { useCallback, useEffect, useRef, useState } from 'react';
import {
  helpdeskAdminApi,
  type HelpdeskTemplate,
  type TemplateVariable,
  type HelpdeskAccount,
} from '../../api/helpdesk-admin';
import { Button } from '../../components/arc/button/button';
import { CompactSelect } from '../../components/arc/compact-select/compact-select';
import { ArcPanel } from '../../components/arc/primitives/arc-primitives';

const CODE_LABEL: Record<string, string> = {
  ticket_received: 'Přijetí dotazu (auto-reply)',
  status_changed: 'Změna stavu ticketu',
  assignee_changed: 'Přiřazení interního operátora',
  reply_greeting: 'Pozdrav v odpovědi',
};
const CODES = Object.keys(CODE_LABEL);
const LANG_FLAG: Record<string, string> = { cs: '🇨🇿 Čeština', sk: '🇸🇰 Slovenština', ro: '🇷🇴 Rumunština', en: '🇬🇧 Angličtina' };

function errText(e: unknown): string {
  const code = (e as { code?: string })?.code;
  return code ? `Chyba: ${code}` : 'Něco se nepovedlo';
}

export function SablonyTab({ accounts }: { accounts: HelpdeskAccount[] }) {
  const [templates, setTemplates] = useState<HelpdeskTemplate[]>([]);
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    helpdeskAdminApi.listTemplates()
      .then((r) => {
        setTemplates(r.templates);
        setVariables(r.variables);
        setSelectedId((cur) => cur ?? r.templates[0]?.id ?? null);
      })
      .catch((e) => setErr(errText(e)));
  }, []);
  useEffect(load, [load]);

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  return (
    <div>
      <div className="hn-tab-actions">
        <Button type="button" variant="primary" size="sm" onClick={() => setCreating(true)}>Nová šablona</Button>
      </div>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-rules-grid">
        <div className="hn-rules-list">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`hn-rules-item${t.id === selectedId ? ' active' : ''}${t.enabled ? '' : ' inactive'}`}
              onClick={() => setSelectedId(t.id)}
            >
              <span>{t.enabled ? '✅' : '⚪'}</span>
              <span>
                <span className="hn-rules-title">{CODE_LABEL[t.code] ?? t.code}</span>
                <span className="hn-meta"> · {LANG_FLAG[t.language] ?? t.language} · {t.account_name ?? 'Výchozí'}</span>
              </span>
            </button>
          ))}
          {templates.length === 0 && <div className="hn-empty">Zatím žádné šablony.</div>}
        </div>
        <div>
          {creating ? (
            <TemplateCreate
              accounts={accounts}
              onClose={() => setCreating(false)}
              onSaved={(id) => { setCreating(false); setSelectedId(id); load(); }}
            />
          ) : selected ? (
            <TemplateEditor key={selected.id} tpl={selected} variables={variables} accounts={accounts} onSaved={load} />
          ) : (
            <div className="hn-empty">Vyber nebo vytvoř šablonu.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ tpl, variables, accounts, onSaved }: { tpl: HelpdeskTemplate; variables: TemplateVariable[]; accounts: HelpdeskAccount[]; onSaved: () => void }) {
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body_plain);
  const [enabled, setEnabled] = useState(!!tpl.enabled);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [preview, setPreview] = useState<{ subjectRendered: string; bodyHtmlRendered: string; fullEmailHtml: string } | null>(null);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const account = tpl.account_id ? accounts.find((a) => a.id === tpl.account_id) : null;
  const [status, setStatus] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const insertVar = (key: string) => {
    const ta = bodyRef.current;
    const token = `{{${key}}}`;
    if (!ta) { setBody((b) => b + token); return; }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
  };

  const save = async () => {
    setStatus('Ukládám…');
    try { await helpdeskAdminApi.updateTemplate(tpl.id, { subject, bodyPlain: body, enabled }); setStatus('✓ Uloženo'); onSaved(); }
    catch (e) { setStatus(errText(e)); }
  };
  const doPreview = async () => {
    try {
      setPreview(await helpdeskAdminApi.previewTemplate({ subject, bodyPlain: body, accountId: tpl.account_id ?? null }));
      setMode('preview');
    } catch (e) { setStatus(errText(e)); }
  };

  return (
    <ArcPanel className="hn-card">
      <div className="hn-form-inline">
        <code className="hn-code">{tpl.code}</code>
        <label className="hn-check"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> {enabled ? '✅ Aktivní' : '⚪ Vypnuto'}</label>
        <span className="hn-tabswitch">
          <button type="button" className={mode === 'edit' ? 'active' : ''} onClick={() => setMode('edit')}>✍️ Editace</button>
          <button type="button" className={mode === 'preview' ? 'active' : ''} onClick={doPreview}>Náhled</button>
        </span>
      </div>

      {mode === 'edit' ? (
        <>
          <FormRow label="Předmět"><input value={subject} onChange={(e) => setSubject(e.target.value)} /></FormRow>
          <FormRow label="Tělo (plain text)"><textarea ref={bodyRef} rows={10} value={body} onChange={(e) => setBody(e.target.value)} /></FormRow>
          <div className="hn-var-chips">
            {variables.map((v) => (
              <button key={v.key} type="button" className="hn-var-chip" title={v.label} onClick={() => insertVar(v.key)}>{`{{${v.key}}}`}</button>
            ))}
          </div>
        </>
      ) : (
        <EmailPreviewCard
          subject={preview?.subjectRendered ?? ''}
          fullEmailHtml={preview?.fullEmailHtml ?? ''}
          fromName={account?.name ?? 'Podpora'}
          fromEmail={account?.email ?? 'podpora@example.com'}
          device={device}
          onDeviceChange={setDevice}
        />
      )}

      <div className="hn-form-buttons">
        <Button type="button" variant="primary" size="sm" onClick={save}>Uložit změny</Button>
        <span className="hn-rule-status">{status}</span>
      </div>
    </ArcPanel>
  );
}

function TemplateCreate({ accounts, onClose, onSaved }: { accounts: HelpdeskAccount[]; onClose: () => void; onSaved: (id: number) => void }) {
  const [code, setCode] = useState(CODES[0] ?? 'ticket_received');
  const [language, setLanguage] = useState('cs');
  const [accountId, setAccountId] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [err, setErr] = useState('');

  const save = async () => {
    setErr('');
    try {
      const { id } = await helpdeskAdminApi.createTemplate({
        code, language, accountId: accountId ? Number(accountId) : null, subject, bodyPlain: body, enabled: true,
      });
      onSaved(id);
    } catch (e) { setErr(errText(e)); }
  };

  return (
    <ArcPanel className="hn-card">
      <h6>Nová šablona</h6>
      <FormRow label="Typ">
        <CompactSelect className="hn-compact-select" value={code} onChange={setCode} ariaLabel="Typ šablony" options={CODES.map((value) => ({ value, label: CODE_LABEL[value] ?? value }))} />
      </FormRow>
      <FormRow label="Jazyk">
        <CompactSelect className="hn-compact-select" value={language} onChange={setLanguage} ariaLabel="Jazyk šablony" options={Object.entries(LANG_FLAG).map(([value, label]) => ({ value, label }))} />
      </FormRow>
      <FormRow label="Schránka">
        <CompactSelect className="hn-compact-select" value={accountId} onChange={setAccountId} ariaLabel="Schránka" options={[{ value: '', label: 'Výchozí (všechny)' }, ...accounts.map((a) => ({ value: String(a.id), label: a.name }))]} />
      </FormRow>
      <FormRow label="Předmět"><input value={subject} onChange={(e) => setSubject(e.target.value)} /></FormRow>
      <FormRow label="Tělo (plain text)"><textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} /></FormRow>
      {err && <div className="hn-err">{err}</div>}
      <div className="hn-form-buttons">
        <Button type="button" variant="primary" size="sm" onClick={save}>Vytvořit</Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>Zrušit</Button>
      </div>
    </ArcPanel>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="hn-form-row"><label>{label}</label><div>{children}</div></div>;
}

function EmailPreviewCard({
  subject,
  fullEmailHtml,
  fromName,
  fromEmail,
  device,
  onDeviceChange,
}: {
  subject: string;
  fullEmailHtml: string;
  fromName: string;
  fromEmail: string;
  device: 'desktop' | 'mobile';
  onDeviceChange: (d: 'desktop' | 'mobile') => void;
}) {
  return (
    <div className="hn-mail-preview">
      <div className="hn-mail-preview-toolbar">
        <div className="hn-mail-preview-title">Náhled e-mailu, jak ho uvidí příjemce</div>
        <div className="hn-mail-preview-devices">
          <button type="button" className={device === 'desktop' ? 'active' : ''} onClick={() => onDeviceChange('desktop')}>Desktop</button>
          <button type="button" className={device === 'mobile' ? 'active' : ''} onClick={() => onDeviceChange('mobile')}>Mobil</button>
        </div>
      </div>
      <div className={`hn-mail-preview-frame hn-device-${device}`}>
        <div className="hn-mail-header">
          <div className="hn-mail-header-row">
            <span className="hn-mail-header-label">Od</span>
            <span className="hn-mail-header-value"><strong>{fromName}</strong> &lt;{fromEmail}&gt;</span>
          </div>
          <div className="hn-mail-header-row">
            <span className="hn-mail-header-label">Komu</span>
            <span className="hn-mail-header-value">Jan Novák &lt;jan.novak@example.com&gt;</span>
          </div>
          <div className="hn-mail-header-row">
            <span className="hn-mail-header-label">Předmět</span>
            <span className="hn-mail-header-value hn-mail-subject">{subject || <em>(bez předmětu)</em>}</span>
          </div>
        </div>
        <iframe
          title="Náhled e-mailu"
          className="hn-mail-body-iframe"
          sandbox="allow-same-origin"
          srcDoc={fullEmailHtml}
        />
      </div>
      <div className="hn-mail-preview-hint">
        Ukázková data: &#123;&#123;vocative&#125;&#125; = Jane, &#123;&#123;from_name&#125;&#125; = Jan Novák, &#123;&#123;ticket_code&#125;&#125; = AM4k9dmz. Skutečné hodnoty se doplní při odeslání.
      </div>
    </div>
  );
}
